import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { checkoutApi } from "../api/checkout";
import AccountMenu from "./AccountMenu";
import PluginsMenu from "./PluginsMenu";
import { PITCHMAN_LAUNCHED } from "../lib/launch";
import { trackBeginCheckout } from "../lib/analytics";
import { getHeroVariant, readVariant, isPaidClick, BUY } from "../lib/experiment";
import { manualHref } from "../i18n/languages";

// Single top bar shared by the marketing landing (/) and the docs (/docs/*).
// It lives in SiteLayout, above the route Outlet, so it stays mounted across
// those navigations — the bar never tears down or reloads. In-shell links use
// <Link> for client-side routing. The primary CTA depends on auth: signed-out
// visitors get "Start Free Trial" (→ /download); signed-in users get "Buy Now",
// which opens a Stripe checkout session. Labels use the "landing" i18n namespace.
export default function SiteNav() {
  const { t, i18n } = useTranslation("landing");
  const { user } = useAuth();
  const location = useLocation();

  // Same tell as the landing's buy-first hero: a paid Google click carries a
  // gclid and has already been quoted the price. Leaving "Start Free Trial" as
  // the loudest button on the page would argue against the hero underneath it,
  // so the bar sells too. See TrapManLanding for why this reads the URL rather
  // than the Merchant Center feed.
  const params = new URLSearchParams(location.search);
  const shoppingIntent = isPaidClick(params);

  // The homepage A/B test moves the bar with the hero, for the same reason:
  // "Start Free Trial" as the loudest button on the page argues against a hero
  // that leads with Buy.
  //
  // Assigned here, during render, and only on the homepage. This bar is part
  // of the shell, so it renders before the landing underneath it: reading a
  // value the landing has not written yet gave a first paint with Buy in the
  // hero and the trial in the bar, which only corrected itself if something
  // else happened to re-render. Assigning in an effect instead would fix the
  // disagreement by showing everyone the control hero for a frame, which is
  // worse — a visible flicker on the page being measured.
  //
  // The write is idempotent (see getHeroVariant), so a double render under
  // StrictMode cannot re-roll it. Off the homepage it only reads, because
  // assigning on /docs or /login would count visitors who never saw a hero.
  const onHomepage = location.pathname === "/";
  const onPitchman = location.pathname === "/pitchman";
  const heroVariant = onHomepage ? getHeroVariant() : readVariant();
  const buyFirst = shoppingIntent || heroVariant === BUY;

  // Opens a Stripe checkout session, guest or account-bound depending on auth,
  // and falls back to the page that at least allows buying if Stripe is down.
  const handleBuy = async (e) => {
    e.preventDefault();
    try {
      // Signed-in visitors go straight to Stripe from here, so this counts as a
      // started checkout just like the landing CTA does.
      trackBeginCheckout({ step: user ? "stripe_session" : "stripe_guest", page: "nav" });
      const resp = user
        ? await checkoutApi.createSession()
        : await checkoutApi.createGuestSession();
      window.location.href = resp.data.url;
    } catch {
      window.location.href = user ? "/dashboard" : "/signup";
    }
  };

  return (
    <nav className="nav">
      <div className="nav-inner">
        <Link to="/" className="logo"><img src="/voleor-logo.png" alt="Voleor" /></Link>
        <div className="nav-links">
          {PITCHMAN_LAUNCHED ? <PluginsMenu /> : <Link to="/">{t("nav.plugin")}</Link>}
          <a href={manualHref(i18n.language)} className="nav-features">{t("nav.manual")}</a>
          <Link to="/docs/" className={PITCHMAN_LAUNCHED ? "nav-docs" : undefined}>{t("nav.docs")}</Link>
          {/* On phones Kits takes Pricing's place (App.css): the header only
              has room for so many links, and the pricing section is a scroll
              away on the landing anyway. */}
          <Link to="/kits" className="nav-kits">{t("nav.kits")}</Link>
          <Link to="/#pricing" className="nav-pricing">{t("nav.pricing")}</Link>
        </div>
        <div className="nav-cta">
          {/* On a free product's page the loudest button is that product,
              whoever is signed in: selling TrapMan above a PitchMan download
              would pull the visitor off the page they came for. */}
          {onPitchman ? (
            <>
              <a href="#ways" className="btn btn-primary">{t("nav.getPitchman")}</a>
              {user ? <AccountMenu triggerClassName="btn" /> : <a href="/login" className="btn">{t("nav.login")}</a>}
            </>
          ) : user ? (
            <>
              <a href="/dashboard" onClick={handleBuy} className="btn btn-primary">{t("nav.buyNow")}</a>
              <AccountMenu triggerClassName="btn" />
            </>
          ) : (
            <>
              {buyFirst ? (
                <a href="/signup" onClick={handleBuy} className="btn btn-primary">{t("nav.buyNow")}</a>
              ) : (
                <a href="/download" className="btn btn-primary">{t("hero.ctaTrial")}</a>
              )}
              <a href="/login" className="btn">{t("nav.login")}</a>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
